import Vue from "vue"
import axios from "axios"
import { AxiosRequestConfig, CancelTokenSource } from "axios"

const subdomain = window.location.host.split(".")[0]
const hostWithCurrentSubdomain =
  process.env.NODE_ENV === "production"
    ? `https://${window.location.host}`
    : process.env.API_URL_TEMPLATE.toString().replace("{subdomain}", subdomain)
const instance = axios.create({
  baseURL: hostWithCurrentSubdomain,
  withCredentials: true,
  headers: { credentials: "same-origin" },
})

window.addEventListener("DOMContentLoaded", () => {
  const csrfToken = document
    .querySelector("meta[name=csrf-token]")
    ?.getAttribute("content")
  if (csrfToken) {
    Vue.prototype.$http.defaults.headers.common["X-CSRF-Token"] = csrfToken
    Vue.prototype.$http.defaults.headers.common["Accept"] = "application/json"
  }
})

const cancelledDueToBrowserDownload = "Download is being handled manually"

// Intercept requests that request a zip file as blob via GET and trigger a download in the browser instead.
// The original download is cancelled and the cancelled requests is intercepted and resolved with an empty response.
// Pass the following options to the api call to download it in the browser, e.g.
//   highlightsApi.downloadHighlights("42", { responseType: "blob", headers: { Accept: "application/zip" } })
instance.interceptors.request.use(
  (config) => {
    // Check if the request should trigger a download
    if (config?.method === "get" && config?.responseType === "blob") {
      triggerDownload(config.url)
      const source: CancelTokenSource = axios.CancelToken.source()
      source.cancel(cancelledDueToBrowserDownload)
      return Promise.resolve({ ...config, cancelToken: source.token })
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Function to trigger download in the browser
const triggerDownload = (url: string) => {
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", "")
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// Intercept responses that are cancelled due to a browser download and resolve them with an empty response
instance.interceptors.response.use(
  (response) => {
    // If the response is successful, just pass it through
    return response
  },
  (error) => {
    // If the error message is "Download is being handled manually", return a successful empty response
    if (
      axios.isCancel(error) &&
      error.message === cancelledDueToBrowserDownload
    ) {
      return {}
    }
    // If it's another error, just throw it
    return Promise.reject(error)
  }
)

/*
 * When the user navigates away, currently pending requests are canceled. This results in ugly error
 * messages for the user and also in error monitoring in NewRelic. The workaround is to detect when the user navigates away
 * and delay the error propagation for a few seconds. This way, neither the user sees the error message nor NewRelic does.
 */
let isNavigatingAway = false

// Detect when navigation is happening
window.addEventListener("beforeunload", () => (isNavigatingAway = true))

document.addEventListener("visibilitychange", () =>
  document.visibilityState === "hidden" ? (isNavigatingAway = true) : null
)

instance.interceptors.response.use(
  (response) => response, // Pass successful responses through
  (error) => {
    if (error.code === "ECONNABORTED" && isNavigatingAway) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (!document.hidden) {
            reject(error) // Propagate the error only if the page is still active
          }
        }, 5000) // Delay error propagation for 5s
      })
    }

    return Promise.reject(error)
  }
)

Vue.prototype.$http = instance

export default instance
